import { useState } from 'react';
import { Card } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { CheckCircle2, Percent, Calendar, Banknote, Star } from 'lucide-react';
import { motion } from 'framer-motion';

const bankOffers = [
  {
    id: 'snb',
    name: 'البنك الأهلي السعودي',
    logo: '/images/logos/snb.png',
    interestRate: 4.75,
    monthlyPayment: 8500,
    totalAmount: 2550000,
    tenure: 25,
    featured: true,
    color: 'from-green-500 to-green-600'
  },
  {
    id: 'anb',
    name: 'البنك العربي الوطني',
    logo: '/images/logos/anb.png',
    interestRate: 4.99,
    monthlyPayment: 8750,
    totalAmount: 2625000,
    tenure: 25,
    featured: false,
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'rajhi',
    name: 'مصرف الراجحي',
    logo: '/images/logos/alrajhi.png',
    interestRate: 4.85,
    monthlyPayment: 8600,
    totalAmount: 2580000,
    tenure: 25,
    featured: false,
    color: 'from-emerald-500 to-teal-600'
  }
];

export default function BankOffers({ onSelect }) {
  const [selectedBank, setSelectedBank] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-slate-50 to-slate-100"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">مبروك! أنت مؤهل للتمويل</h1>
          <p className="text-slate-500">اختر العرض المناسب لك من البنوك التالية</p>
        </div>

        <div className="grid gap-6">
          {bankOffers.map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${
                  selectedBank === offer.id
                    ? 'ring-2 ring-emerald-500 shadow-xl'
                    : 'hover:shadow-lg border-slate-100'
                }`}
                onClick={() => setSelectedBank(offer.id)}
              >
                {offer.featured && (
                  <div className="absolute top-0 left-0 right-0">
                    <div className={`bg-gradient-to-r ${offer.color} text-white text-xs font-medium py-1.5 px-4 flex items-center justify-center gap-1`}>
                      <Star className="w-3 h-3" />
                      أفضل عرض
                    </div>
                  </div>
                )}

                <div className={`p-6 ${offer.featured ? 'pt-10' : ''}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    {/* Bank Info */}
                    <div className="flex items-center gap-4 md:w-1/4">
                      <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100 p-2">
                        <img src={offer.logo} alt={offer.name} className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{offer.name}</h3>
                        <Badge variant="secondary" className="mt-1">{offer.tenure} سنة</Badge>
                      </div>
                    </div>

                    {/* Offer Details */}
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center justify-center gap-1 text-slate-500 mb-1">
                          <Percent className="w-4 h-4" />
                          <span className="text-xs">نسبة الربح</span>
                        </div>
                        <p className="text-xl font-bold text-slate-900">{offer.interestRate}%</p>
                      </div>

                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center justify-center gap-1 text-slate-500 mb-1">
                          <Calendar className="w-4 h-4" />
                          <span className="text-xs">القسط الشهري</span>
                        </div>
                        <p className="text-xl font-bold text-emerald-600">{formatCurrency(offer.monthlyPayment)}</p>
                      </div>

                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center justify-center gap-1 text-slate-500 mb-1">
                          <Banknote className="w-4 h-4" />
                          <span className="text-xs">إجمالي السداد</span>
                        </div>
                        <p className="text-lg font-bold text-slate-900">{formatCurrency(offer.totalAmount)}</p>
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    <div className="md:w-auto">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedBank === offer.id
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-slate-300'
                      }`}>
                        {selectedBank === offer.id && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="mt-8">
          <Button
            onClick={() => onSelect(bankOffers.find(o => o.id === selectedBank))}
            disabled={!selectedBank}
            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium disabled:opacity-50"
          >
            الموافقة على العرض
          </Button>
        </div>
      </div>
    </motion.div>
  );
}